import {
  GPT_IMAGE_25_QUALITIES,
  GPT_IMAGE_2_SIZES,
  GPT_IMAGE_QUALITIES,
  IMAGE_RESOLUTIONS,
  imageModelLabel,
  type GPTImageQuality,
  type ImageResolution,
} from '@inference.engine/widgets/web';
import { i18n } from '@/shared/i18n';

export type { ImageResolution };

export type AspectRatioOption = {
  label: string;
  value: string;
  orientation?: 'portrait' | 'landscape';
};

export type ReferenceAccuracy = 'high' | 'low';

export type ImageQuality = GPTImageQuality;

type Mask = {
  assetId: string;
  metadata: { size: number; fileName: string };
  mimeType: 'image/png';
};

// Getters, not values: these tables are built while the module is evaluated, so resolving per
// read is what lets a label follow a language change instead of freezing at import.
/* eslint-disable i18next/no-literal-string -- wire enum values for accuracy and quality */
export const REFERENCE_ACCURACY_OPTIONS = [
  {
    get label() {
      return i18n.t('chats:chats.imageGenerationOptions.referenceAccuracyHigh');
    },
    value: 'high' as const,
  },
  {
    get label() {
      return i18n.t('chats:chats.imageGenerationOptions.referenceAccuracyLow');
    },
    value: 'low' as const,
  },
] as const;

export const QUALITY_OPTIONS = [
  {
    get label() {
      return i18n.t('chats:chats.imageGenerationOptions.qualityAuto');
    },
    value: 'auto' as const,
  },
  {
    get label() {
      return i18n.t('chats:chats.imageGenerationOptions.qualityLow');
    },
    value: 'low' as const,
  },
  {
    get label() {
      return i18n.t('chats:chats.imageGenerationOptions.qualityMedium');
    },
    value: 'medium' as const,
  },
  {
    get label() {
      return i18n.t('chats:chats.imageGenerationOptions.qualityHigh');
    },
    value: 'high' as const,
  },
  {
    get label() {
      return i18n.t('chats:chats.imageGenerationOptions.qualityXhigh');
    },
    value: 'xhigh' as const,
  },
  {
    get label() {
      return i18n.t('chats:chats.imageGenerationOptions.qualityMax');
    },
    value: 'max' as const,
  },
] as const;
/* eslint-enable i18next/no-literal-string */

export type ModelFeature = 'create' | 'edit' | 'edit-with-mask';

/* eslint-disable i18next/no-literal-string -- vendor model and provider names */
export const SUPPORTED_IMAGE_MODELS = [
  {
    label: imageModelLabel('gemini-nano-banana-2.1'),
    value: 'gemini-nano-banana-2.1' as const,
    provider: 'Google',
    features: ['create', 'edit', 'edit-with-mask'] as const,
  },
  {
    label: imageModelLabel('gemini-3-pro-image'),
    value: 'gemini-3-pro-image' as const,
    provider: 'Google',
    features: ['create', 'edit', 'edit-with-mask'] as const,
  },
  {
    label: imageModelLabel('gemini-3.1-flash-image'),
    value: 'gemini-3.1-flash-image' as const,
    provider: 'Google',
    features: ['create', 'edit', 'edit-with-mask'] as const,
  },
  {
    label: imageModelLabel('dall-e-3'),
    value: 'dall-e-3' as const,
    provider: 'OpenAI',
    features: ['create'] as const,
  },
  {
    label: imageModelLabel('gpt-image-1'),
    value: 'gpt-image-1' as const,
    provider: 'OpenAI',
    features: ['create', 'edit', 'edit-with-mask'] as const,
  },
  {
    label: imageModelLabel('gpt-image-2.5-sunburst'),
    value: 'gpt-image-2.5-sunburst' as const,
    provider: 'OpenAI',
    features: ['create', 'edit', 'edit-with-mask'] as const,
  },
  {
    label: imageModelLabel('gpt-image-2.5-flare'),
    value: 'gpt-image-2.5-flare' as const,
    provider: 'OpenAI',
    features: ['create', 'edit', 'edit-with-mask'] as const,
  },
  {
    label: imageModelLabel('gpt-image-2'),
    value: 'gpt-image-2' as const,
    provider: 'OpenAI',
    features: ['create', 'edit', 'edit-with-mask'] as const,
  },
  {
    label: imageModelLabel('firefly-v3'),
    value: 'firefly-v3' as const,
    provider: 'Adobe',
    features: ['create', 'edit'] as const,
  },
  {
    label: imageModelLabel('firefly-v4-standard'),
    value: 'firefly-v4-standard' as const,
    provider: 'Adobe',
    features: ['create', 'edit'] as const,
  },
  /* eslint-enable i18next/no-literal-string */
] as {
  label: string;
  value: string;
  provider: string;
  features: readonly ModelFeature[];
}[];

const GEMINI_COMMON_ASPECT_RATIOS: AspectRatioOption[] = [
  { label: '1:1', value: '1:1', orientation: 'portrait' },
  { label: '2:3', value: '2:3', orientation: 'portrait' },
  { label: '3:2', value: '3:2', orientation: 'landscape' },
  { label: '3:4', value: '3:4', orientation: 'portrait' },
  { label: '4:3', value: '4:3', orientation: 'landscape' },
  { label: '4:5', value: '4:5', orientation: 'portrait' },
  { label: '5:4', value: '5:4', orientation: 'landscape' },
  { label: '9:16', value: '9:16', orientation: 'portrait' },
  { label: '16:9', value: '16:9', orientation: 'landscape' },
  { label: '21:9', value: '21:9', orientation: 'landscape' },
];

const GOOGLE_3_PRO_ASPECT_RATIOS: AspectRatioOption[] = GEMINI_COMMON_ASPECT_RATIOS;

const GEMINI_WIDE_ASPECT_RATIOS: AspectRatioOption[] = [
  ...GEMINI_COMMON_ASPECT_RATIOS,
  { label: '1:4', value: '1:4', orientation: 'portrait' },
  { label: '1:8', value: '1:8', orientation: 'portrait' },
  { label: '4:1', value: '4:1', orientation: 'landscape' },
  { label: '8:1', value: '8:1', orientation: 'landscape' },
];

/* eslint-disable i18next/no-literal-string -- pixel dimension identifiers, not translatable copy */
const GPT_IMAGE_2_SIZE_OPTIONS: AspectRatioOption[] = GPT_IMAGE_2_SIZES.map((size) => ({
  value: size,
  label: size,
}));

export const IMAGE_MODEL_ASPECT_RATIOS: Record<
  ImageModel | 'custom-workflow',
  AspectRatioOption[]
> = {
  'firefly-v3': [
    { value: '2048x2048', label: '2048x2048' },
    { value: '1024x1024', label: '1024x1024' },
    { value: '2304x1792', label: '2304x1792' },
    { value: '1792x2304', label: '1792x2304' },
    { value: '2688x1536', label: '2688x1536' },
    { value: '2688x1512', label: '2688x1512' },
    { value: '1344x768', label: '1344x768' },
    { value: '1344x756', label: '1344x756' },
    { value: '1152x896', label: '1152x896' },
    { value: '896x1152', label: '896x1152' },
  ],
  'firefly-v4-standard': [
    { value: '2048x2048', label: '2048x2048' },
    { value: '2304x1792', label: '2304x1792' },
    { value: '1792x2304', label: '1792x2304' },
    { value: '2688x1536', label: '2688x1536' },
    { value: '1440x2560', label: '1440x2560' },
  ],
  'gpt-image-1': [
    { value: '1024x1024', label: '1024x1024' },
    { value: '1024x1536', label: '1024x1536' },
    { value: '1536x1024', label: '1536x1024' },
  ],
  'gpt-image-2': GPT_IMAGE_2_SIZE_OPTIONS,
  'gpt-image-2.5-sunburst': GPT_IMAGE_2_SIZE_OPTIONS,
  'gpt-image-2.5-flare': GPT_IMAGE_2_SIZE_OPTIONS,
  'dall-e-3': [
    { value: '1024x1024', label: '1024x1024' },
    { value: '1792x1024', label: '1792x1024' },
    { value: '1024x1792', label: '1024x1792' },
  ],
  'gemini-3.1-flash-image': GEMINI_WIDE_ASPECT_RATIOS,
  'gemini-nano-banana-2.1': GEMINI_WIDE_ASPECT_RATIOS,
  'gemini-3-pro-image': GOOGLE_3_PRO_ASPECT_RATIOS,
  'custom-workflow': [],
} as const;
/* eslint-enable i18next/no-literal-string */

export const aspectRatioModelsMap: ImageModel[] = [
  'gemini-3.1-flash-image',
  'gemini-nano-banana-2.1',
  'gemini-3-pro-image',
] as const;

export const referenceAccuracyModelsMap: ImageModel[] = ['gpt-image-1'] as const;

export const IMAGE_MODEL_QUALITIES: Partial<Record<ImageModel, readonly ImageQuality[]>> = {
  'gpt-image-2': GPT_IMAGE_QUALITIES,
  'gpt-image-2.5-sunburst': GPT_IMAGE_25_QUALITIES,
  'gpt-image-2.5-flare': GPT_IMAGE_25_QUALITIES,
};

export const qualityModelsMap = Object.keys(IMAGE_MODEL_QUALITIES) as ImageModel[];

export const resolutionModelsMap: ImageModel[] = [
  'gemini-3-pro-image',
  'gemini-3.1-flash-image',
  'gemini-nano-banana-2.1',
] as const;

export const IMAGE_MODEL_RESOLUTIONS: Partial<Record<ImageModel, readonly ImageResolution[]>> = {
  'gemini-3-pro-image': IMAGE_RESOLUTIONS,
  'gemini-3.1-flash-image': ['1K', '2K'],
  'gemini-nano-banana-2.1': IMAGE_RESOLUTIONS,
};

export const customModelIdModelsMap: ImageModel[] = ['firefly-v3', 'firefly-v4-standard'] as const;

export type ImageOptions = {
  settings: {
    model: ImageModel | 'custom-workflow';
    aspectRatio?: string;
    resolution?: ImageResolution;
    size?: string;
    referenceAccuracy?: ReferenceAccuracy;
    quality?: ImageQuality;
    customModelId?: string;
    customWorkflowId?: string;
    customWorkflowInputs?: Record<string, string | number>;
  };
  variations: number;
  mask?: Mask;
};

export type ImageModel =
  | 'gemini-3.1-flash-image'
  | 'gemini-nano-banana-2.1'
  | 'gemini-3-pro-image'
  | 'dall-e-3'
  | 'gpt-image-1'
  | 'gpt-image-2'
  | 'gpt-image-2.5-sunburst'
  | 'gpt-image-2.5-flare'
  | 'firefly-v3'
  | 'firefly-v4-standard';

export const AMOUNT_OF_VARIATIONS_OPTIONS = [
  {
    label: '1x',
    value: 1,
  },
  {
    label: '2x',
    value: 2,
  },
  {
    label: '3x',
    value: 3,
  },
  {
    label: '4x',
    value: 4,
  },
];
